import { Component, EventListener, findComponents, getComponentByDomNode, triggerPublicEvent } from '@gondel/core';
import '@swisslife/patterns-base/src/atoms/image/js/image';
import { Image } from '@swisslife/patterns-base/src/atoms/image/js/image';
import { GondelJqueryComponent } from '@gondel/plugin-jquery';
import $ from 'jquery';
import { Header } from '../../../organisms/header/js/header';
import { stateAnimationVisible } from '@swisslife/shared-ui/src/utils/animations/js/animations';
import { IGOverlayHandlerOptions } from '@swisslife/shared-types/src/types/gondel-public-events';
import objectFitImages from 'object-fit-images';

enum States {
	Animate = 'state-m-mood-image-conversion--animate',
	Scroll = 'state-m-mood-image-conversion--scroll',
	QuickLinksOpen = 'state-m-mood-image-conversion--quick-links-open',
	DisableAnimation = 'state-h-animation--disabled',
}
type TStates = keyof typeof States;

enum Selectors {
	Main = '.js-l-main',
	Image = '.js-m-mood-image__image',
	Header = '.js-o-header',
	ContentAnimation = '.js-m-mood-image-conversion__content-animation',
	OverlayButton = '.js-m-mood-image-conversion__trigger-overlay',
	CertificateImage = '.js-m-mood-image-conversion__certificate-wrapper .js-a-image__image',
}

@Component('MoodImageConversion')
export class MoodImageConversion extends GondelJqueryComponent {
	private image: Image | null = null;
	private controllers: any[] = Array();
	private header: Header;

	public start() {
		const $moodImage = $(Selectors.Image, this.$ctx);
		if ($moodImage.length > 0) {
			const imageComponents = findComponents<Image>($moodImage.get(0), 'Image');
			if (imageComponents.length > 0) {
				this.image = imageComponents[0];
			}
		}

		// IE11 workaround
		const $certificateImage = $(Selectors.CertificateImage, this.$ctx);
		if ($certificateImage.length > 0) {
			objectFitImages(Selectors.CertificateImage);
		}

		const $header = this.$ctx.closest(Selectors.Header);
		if ($header.length > 0) {
			this.header = getComponentByDomNode<Header>($header.get(0));
		}
	}

	public async sync() {
		// check if animation should be enabled
		if (!$('body').hasClass(States.DisableAnimation)) {
			this.initializeFadeAnimation();
			await this.initializeImageAnimation();
			await this.initializeContentAnimation();
		}
	}

	public stop() {
		for (const controller of this.controllers) {
			controller.destroy(true);
		}
	}

	public toggleStateClass(stateToToggle: TStates, forceState?: boolean) {
		if (forceState !== undefined) {
			this.$ctx.toggleClass(States[stateToToggle], forceState);
		} else {
			this.$ctx.toggleClass(States[stateToToggle]);
		}
	}

	public removeStateClass(stateToToggle: TStates) {
		this.$ctx.removeClass(States[stateToToggle]);
	}

	private initializeFadeAnimation() {
		if (this.image !== null) {
			const promise = this.image.getImageLoadedPromise();
			promise.then(() => {
				this.toggleStateClass('Animate', true);
				this.toggleStateClassOnBreadcrumb();
			});
		} else {
			// if it's a mood with background or video, we start the animation right away
			this.toggleStateClass('Animate', true);
			this.toggleStateClassOnBreadcrumb();
		}
	}

	private toggleStateClassOnBreadcrumb() {
		if (this.header && this.header.breadcrumb) {
			this.header.breadcrumb.toggleStateClass('Animate', true);
		}
	}

	private async initializeContentAnimation() {
		// this.controllers.push(await addComponentAnimation(this.$ctx, Selectors.ContentAnimation))
		const $content = this.$ctx.find(Selectors.ContentAnimation);

		// tslint:disable
		const {
			TimelineMax,
			ScrollMagic,
		} = (await import(/* webpackChunkName: "dynamic-imports/animation-libs" */ '@swisslife/shared-ui/src/utils/animations/js/animation-libs')) as any;
		// tslint:enable

		const controller = new ScrollMagic.Controller();
		const timelineContent = new TimelineMax();

		timelineContent.to($content, 1, { delay: 1, css: { className: '+=' + stateAnimationVisible } });

		const sceneContent = new ScrollMagic.Scene();
		// .addIndicators() // add visual indicators to your page, to be able to see exactly when a scene is triggered

		sceneContent.setTween(timelineContent);
		controller.addScene([sceneContent]);
		this.controllers.push(controller);
	}

	private async initializeImageAnimation() {
		const $image = this.$ctx.find(Selectors.Image);

		// tslint:disable
		const {
			TimelineMax,
			Linear,
			ScrollMagic,
		} = (await import(/* webpackChunkName: "dynamic-imports/animation-libs" */ '@swisslife/shared-ui/src/utils/animations/js/animation-libs')) as any;
		// tslint:enable

		const controller = new ScrollMagic.Controller();
		const timelineImage = new TimelineMax();

		timelineImage.to($image, 1, { y: 200, ease: Linear.easeIn });
		timelineImage.eventCallback('onStart', () => {
			this.toggleStateClass('Scroll', true);
		});
		timelineImage.eventCallback('onReverseComplete', () => {
			this.toggleStateClass('Scroll', false);
		});

		const sceneImage = new ScrollMagic.Scene({
			triggerHook: 'onLeave',
			duration: '100%',
		});
		// .addIndicators() // add visual indicators to your page, to be able to see exactly when a scene is triggered

		sceneImage.setTween(timelineImage);
		controller.addScene([sceneImage]);
		this.controllers.push(controller);
	}

	@EventListener('click', Selectors.OverlayButton)
	private _handleButtonClick(ev: MouseEvent) {
		ev.preventDefault();

		const $target = $(ev.currentTarget!);

		const eventOptions: IGOverlayHandlerOptions = {
			overlayComponent: 'MoodImageConversion',
			overlayTitle: $target.data('overlay-title') || '',
			overlayContentUrl: $target.data('overlay-content-url') || '',
		};

		triggerPublicEvent('gOverlayHandler', this, undefined, eventOptions);
	}
}
